/**
 * Auxiliary (extras + insurance) Availability Codes
 *
 * Single source of truth for the Bewotec `<Availability>` field semantics shared
 * by PPS extras and SSC insurances. The cart's "extras" step renders both kinds
 * with the same UI rules, so the parsing lives here rather than per-schema.
 *
 * | Raw  | Bewotec meaning              | UI behaviour                       |
 * |------|------------------------------|------------------------------------|
 * | C    | Category closed              | Hide                               |
 * | -    | Not available in category    | Hide                               |
 * | E    | External (not loaded)        | Hide (Bewotec: not used by VAMOS)  |
 * | *    | More than 9 available        | Show, no flow change               |
 * | 1-9  | Specific count available     | Show, no flow change               |
 * | R    | On request                   | Show, switch booking flow to BQ    |
 *
 * Anything else falls back to `'unavailable'` defensively (rendered greyed-out,
 * no CTA) so unknown future codes don't accidentally become bookable.
 */

export type AuxiliaryAvailabilityState = 'hidden' | 'available' | 'on-request' | 'unavailable';

const HIDDEN_CODES = new Set(['C', '-', 'E']);
const AVAILABLE_NUMERIC = /^[1-9]$/;

export function parseAuxiliaryAvailability(raw: string): AuxiliaryAvailabilityState {
  const code = raw.trim();
  if (HIDDEN_CODES.has(code)) return 'hidden';
  if (code === '*' || AVAILABLE_NUMERIC.test(code)) return 'available';
  if (code === 'R') return 'on-request';
  return 'unavailable';
}

export function isHiddenAuxiliaryAvailability(raw: string): boolean {
  return parseAuxiliaryAvailability(raw) === 'hidden';
}
